import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Edit,
  UserX,
  UserCheck,
  DollarSign,
  Percent,
  TrendingUp,
  CreditCard,
  MapPin,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import EditAgentDialog from '../../components/admin/EditAgentDialog';

export default function AgentDetail() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [agent, setAgent] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [referredStudents, setReferredStudents] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    referredStudents: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    paidCommissions: 0,
    pendingCommissions: 0,
  });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('students');

  useEffect(() => {
    fetchAgentDetails();
  }, [agentId]);

  const fetchAgentDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAgentById(agentId);
      setAgent(response.agent);
      setAssignedStudents(response.assignedStudents || []);
      setReferredStudents(response.referredStudents || []);
      setCommissions(response.commissions || []);
      setStats(response.stats || {
        totalStudents: 0,
        referredStudents: 0,
        totalEarnings: 0,
        pendingEarnings: 0,
        paidCommissions: 0,
        pendingCommissions: 0,
      });
    } catch (error) {
      toast.error('Failed to fetch agent details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    try {
      const newStatus = !agent.isActive;
      await adminAPI.updateAgentStatus(agentId, newStatus);
      toast.success(`Agent ${newStatus ? 'activated' : 'deactivated'}`);
      fetchAgentDetails();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-700' },
      paid: { bg: 'bg-green-100', text: 'text-green-700' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700' },
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700' },
      ASSIGNED: { bg: 'bg-purple-100', text: 'text-purple-700' },
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-700' },
    };
    const config = statusConfig[status] || { bg: 'bg-stone-100', text: 'text-gray-700' };
    return (
      <Badge className={`${config.bg} ${config.text} text-xs`}>
        {status?.replace(/_/g, ' ') || 'Unknown'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!agent) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Agent Not Found</h2>
          <p className="text-gray-500 mb-4">The agent you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/admin/agents')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Agents
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/agents')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Agent Details</h1>
            <p className="text-gray-600">View and manage agent information and commissions</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                {agent.firstName?.[0]}{agent.lastName?.[0]}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {agent.firstName} {agent.lastName}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-green-100 text-green-700">Agent</Badge>
                  <Badge className={`${
                    agent.isActive !== false
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {agent.isActive !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{agent.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{agent.phone || 'Not provided'}</span>
                  </div>
                  {agent.country && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{agent.country}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {formatDate(agent.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission Info */}
            <div className="md:ml-8 p-4 bg-stone-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Commission Settings</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {agent.commissionRate || agent.commissionPercentage || 10}%
                    </p>
                    <p className="text-xs text-gray-500">Commission Rate</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="md:ml-auto flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant={agent.isActive !== false ? 'destructive' : 'default'}
                onClick={handleStatusToggle}
              >
                {agent.isActive !== false ? (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Activate
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Students</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalStudents}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalEarnings)}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Pending Earnings</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.pendingEarnings)}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Referred Students</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.referredStudents}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Card className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="students" className="gap-2">
                <Users className="w-4 h-4" />
                Assigned Students ({assignedStudents.length})
              </TabsTrigger>
              <TabsTrigger value="referred" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                Referred ({referredStudents.length})
              </TabsTrigger>
              <TabsTrigger value="commissions" className="gap-2">
                <CreditCard className="w-4 h-4" />
                Commissions ({commissions.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="students">
              {assignedStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No students assigned</p>
                  <p className="text-sm">Students will appear here once assigned to this agent</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Services</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignedStudents.map((student, index) => (
                        <TableRow key={student._id || student.studentId || index}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {student.user?.firstName?.[0] || student.firstName?.[0]}
                                {student.user?.lastName?.[0] || student.lastName?.[0]}
                              </div>
                              <span className="font-medium">
                                {student.user?.firstName || student.firstName}{' '}
                                {student.user?.lastName || student.lastName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {student.user?.email || student.email}
                          </TableCell>
                          <TableCell>{student.destinationCountry || student.country || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {student.services?.length || 0} service(s)
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {student.services?.[0]?.status && getStatusBadge(student.services[0].status)}
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(student.services?.[0]?.assignedAt || student.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="referred">
              {referredStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <TrendingUp className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No referred students</p>
                  <p className="text-sm">Students referred by this agent will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Joined Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referredStudents.map((student, index) => (
                        <TableRow key={student._id || student.studentId || index}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-[#F2575F] to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {student.user?.firstName?.[0] || student.firstName?.[0]}
                                {student.user?.lastName?.[0] || student.lastName?.[0]}
                              </div>
                              <span className="font-medium">
                                {student.user?.firstName || student.firstName}{' '}
                                {student.user?.lastName || student.lastName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {student.user?.email || student.email}
                          </TableCell>
                          <TableCell>{student.destinationCountry || student.country || 'N/A'}</TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(student.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="commissions">
              {commissions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <CreditCard className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No commission history</p>
                  <p className="text-sm">Commission records will appear here</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Commission ID</TableHead>
                        <TableHead>Student</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissions.map((commission, index) => (
                        <TableRow key={commission.commissionId || commission._id || index}>
                          <TableCell className="font-mono text-sm">
                            {(commission.commissionId || commission._id || '').slice(0, 8)}...
                          </TableCell>
                          <TableCell>{commission.studentName || 'N/A'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{commission.serviceType || 'Service'}</Badge>
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {formatCurrency(commission.amount)}
                          </TableCell>
                          <TableCell>{getStatusBadge(commission.status)}</TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(commission.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Edit Dialog */}
      <EditAgentDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        agent={agent}
        onSuccess={fetchAgentDetails}
      />
    </AdminLayout>
  );
}
